import { Head, useForm, Link } from '@inertiajs/react'
import { FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { AuthLayout } from '@/components/layouts/AuthLayout'

interface UserData {
  email: string
  password: string
  remember_me: boolean
}

interface LoginForm {
  user: UserData
}

const COMMUNITY_LABELS: Record<string, string> = {
  medecins: 'Médecin',
  pharmaciens: 'Pharmacien',
  patients: 'Patient',
}

interface LoginProps {
  community_slug?: string
}

export default function Login({ community_slug = 'medecins' }: LoginProps) {
  const registrationLabel = COMMUNITY_LABELS[community_slug] ?? COMMUNITY_LABELS.medecins
  const registrationSlug = COMMUNITY_LABELS[community_slug] ? community_slug : 'medecins'
  const { data, setData, post, processing, errors } = useForm<LoginForm>({
    user: {
      email: '',
      password: '',
      remember_me: false,
    },
  })

  const setUserData = (field: keyof UserData, value: string | boolean) => {
    setData('user', { ...data.user, [field]: value })
  }

  const userErrors = errors as Record<string, string>

  const submit = (e: FormEvent) => {
    e.preventDefault()
    post('/users/sign_in')
  }

  return (
    <AuthLayout>
      <Head title="Connexion - Tessan Community" />

      <div className="text-center mb-8">
        <h1 className="text-3xl font-heading font-bold text-text-primary">Connexion</h1>
        <p className="text-text-secondary mt-2">Accédez à votre espace communautaire</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Input
          label="Email"
          type="email"
          value={data.user.email}
          onChange={(e) => setUserData('email', e.target.value)}
          error={userErrors['user.email'] || userErrors.email}
          required
          autoFocus
        />

        <Input
          label="Mot de passe"
          type="password"
          value={data.user.password}
          onChange={(e) => setUserData('password', e.target.value)}
          error={userErrors['user.password']}
          required
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.user.remember_me}
              onChange={(e) => setUserData('remember_me', e.target.checked)}
              className="rounded border-gray-300 text-tessan-green focus:ring-tessan-green"
            />
            <span className="text-sm text-text-secondary">Se souvenir de moi</span>
          </label>

          <Link
            href="/users/password/new"
            className="text-sm text-tessan-green hover:underline"
          >
            Mot de passe oublié ?
          </Link>
        </div>

        <Button type="submit" className="w-full" disabled={processing}>
          {processing ? 'Connexion...' : 'Se connecter'}
        </Button>

        <div className="pt-4 border-t border-gray-200 text-center text-sm text-text-secondary">
          Pas encore de compte ?{' '}
          <Link
            href={`/${registrationSlug}/inscription`}
            className="text-tessan-green font-medium hover:underline"
          >
            S'inscrire en tant que {registrationLabel.toLowerCase()}
          </Link>
        </div>
      </form>
    </AuthLayout>
  )
}
